/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { useListServerlessTrafficFiltersQuery } from '@modules/network-security-lib/hooks/serverless'
import {
  usePatchElasticsearchProjectMutation,
  usePatchObservabilityProjectMutation,
  usePatchSecurityProjectMutation,
} from '@modules/project-user-lib/hooks/update'

import type { TrafficFilterAssociationHandlerProps } from './types'
import type { ForwardRefRenderFunction } from 'react'

const ServerlessTrafficFilterAssociationHandler: ForwardRefRenderFunction<
  {
    save: () => void
  },
  TrafficFilterAssociationHandlerProps<'serverless'>
> = ({ onClose, project, onAssociationChange, onFormStateChange, children }, ref) => {
  // Initialization

  const { data } = useListServerlessTrafficFiltersQuery()

  const regionId = project.region_id

  // show only rulesets that match the project's region and aren't already associated
  const filteredRulesets = useMemo(
    () =>
      (data?.items || []).filter(
        (ruleset) =>
          ruleset.region === regionId &&
          !project.traffic_filters?.some(({ id }) => id === ruleset.id),
      ),
    [data?.items, regionId, project.traffic_filters],
  )

  // State changes

  const [rulesetId, setRulesetId] = useState('')

  const isSubmittable = useMemo(() => Boolean(rulesetId), [rulesetId])

  // Saving

  const patchElasticsearchProjectMutation = usePatchElasticsearchProjectMutation()
  const patchObservabilityProjectMutation = usePatchObservabilityProjectMutation()
  const patchSecurityProjectMutation = usePatchSecurityProjectMutation()

  const patchProjectMutation = {
    elasticsearch: patchElasticsearchProjectMutation,
    observability: patchObservabilityProjectMutation,
    security: patchSecurityProjectMutation,
  }[project.type]

  const { mutate: patchProjectMutate, isLoading: isSaving, error } = patchProjectMutation

  const save = () => {
    patchProjectMutate(
      {
        id: project.id,
        body: {
          traffic_filters: [...(project.traffic_filters ?? []), { id: rulesetId }],
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }

  // Parent API

  useImperativeHandle(ref, () => ({
    save,
  }))

  useEffect(() => {
    onAssociationChange({ isSubmittable })
  }, [isSubmittable])

  useEffect(() => {
    onFormStateChange({ isSaving, error })
  }, [isSaving, error])

  // Rendering

  return children({
    rulesets: filteredRulesets,
    rulesetId,
    setRulesetId,
  })
}

export default forwardRef(ServerlessTrafficFilterAssociationHandler)
